import type { Answers, TSuppressionProfile } from "./_schema";
import { deriveIgniteProfile, type ExtraSignals } from "./ignite-profile";

/**
 * Energy-quiz deriver. Wraps `deriveIgniteProfile` (the quiz-v7 / cortisol /
 * weight-loss pattern) but with ONE structural difference: it routes
 * **organically** — no `forcePrimaryCause`.
 *
 * Why organic (operator directive 2026-07-16): the energy angle's villain is
 * low testosterone itself — the same villain the T-suppression verdict surface
 * already diagnoses — not a narrower mechanism with a matching root-cause key
 * (cortisol / visceral-adiposity / catabolic-deficit). Bridge 1 explicitly says
 * "it's not *just* aging", so the flow must not hard-blame a single cause.
 * `pickRootCauses` derives the primary/secondary from the answers, like Ignite.
 *
 *   1. Answer translation into the ignite namespace:
 *        - Passthrough (ignite-verbatim values): `age`, `energy-3pm`,
 *          `when-started`.
 *        - Injected `concern: "energy"` — the entire quiz's concern IS energy,
 *          so this reflects the premise, not a manufactured answer. It adds
 *          NOTHING to tier1/tier2 severity (countSignals reads `concern` only
 *          for sex-drive/muscle/focus).
 *        - `focus-mood` -> `stress-symptoms` and `sleep-quality` ->
 *          `sleep-hours` are genuine construct matches (the ignite stress
 *          question is literally "tired, cranky, foggy, or moody"). They feed
 *          severity + root causes ORGANICALLY via countSignals/scoreRootCauses,
 *          so they are deliberately absent from the extraSignals maps below —
 *          a signal is never counted twice.
 *        - `energy-goal` -> `results-want` feeds the (unrendered)
 *          expected-outcome so the profile stays internally coherent.
 *   2. Sign severity: the 4 energy-specific sign questions feed severity via the
 *      `extraSignals` seam (worst answer -> tier1, next -> tier2/tier3, the
 *      quiz-v7 cohort-gradient convention). `libido` carries tier1 because a
 *      direct libido question is the strongest self-reported low-T correlate and
 *      the gap the base Ignite quiz flags (it has no libido screen).
 *
 * On-angle guard: `body-change` is intentionally NOT translated to
 * `body-composition`. `body-composition ∈ {overweight, soft-some-muscle}` is the
 * only organic trigger for `visceral-adiposity` (the weight-loss villain);
 * leaving it unset — plus `concern: "energy"` not `"weight-loss"` — makes
 * `visceral-adiposity`, `metabolic-syndrome`, and `overtraining` unscoreable, so
 * the organic verdict can only surface on-angle causes (cortisol/chronic-stress,
 * sleep-deprivation, age-related-decline, lifestyle-load). `body-change` still
 * feeds severity (extraSignals) and the classifier. Verified across the full
 * 786,432-path space in tests/quizzes/quiz-energy.test.ts.
 *
 * Pure function of `answers` — reproducible server-side from the persisted
 * submission (rule 11).
 */

/** Ids whose values are ignite-verbatim — copied through untranslated. */
const PASSTHROUGH_IDS = ["age", "energy-3pm", "when-started"] as const;

/** Focus/mood -> ignite stress-symptoms (genuine construct match). */
const FOCUS_TO_STRESS: Record<string, "yes" | "no"> = {
  foggy: "yes",
  "flat-irritable": "yes",
  both: "yes",
  "no-change": "no",
};

/** Sleep quality -> ignite sleep-hours (severity + sleep-deprivation cause). */
const SLEEP_TO_HOURS: Record<string, "lt-5" | "5-6" | "7-8"> = {
  "cant-sleep": "lt-5",
  "wake-through-night": "5-6",
  "wake-tired": "7-8",
  "sleep-fine-still-tired": "7-8",
};

/** Aspiration beat -> ignite results-want (expected-outcome only, unrendered). */
const GOAL_TO_RESULTS: Record<string, "energy" | "all-of-above"> = {
  "stop-dragging": "energy",
  "enjoy-life": "energy",
  "feel-myself": "all-of-above",
};

/**
 * Sign-question severity mapping. Worst answer -> tier1, next -> tier2/tier3
 * (quiz-v7 cohort convention). These are the energy-specific ids WITHOUT a
 * genuine ignite equivalent; the translated ids (`focus-mood`, `sleep-quality`)
 * are absent here because they already feed severity organically.
 */
const TIER1_SIGNS: Record<string, readonly string[]> = {
  "energy-pattern": ["tired-on-waking", "all-of-above"],
  libido: ["significantly"],
  "body-change": ["both"],
};
const TIER2_SIGNS: Record<string, readonly string[]> = {
  "energy-pattern": ["wired-then-wiped", "up-and-down"],
  libido: ["somewhat"],
  "body-change": ["more-belly", "softer-muscle"],
  "motivation-drop": ["significantly"],
};
const TIER3_SIGNS: Record<string, readonly string[]> = {
  "motivation-drop": ["somewhat"],
};

function countTier(answers: Answers, map: Record<string, readonly string[]>): number {
  let n = 0;
  for (const [id, values] of Object.entries(map)) {
    const v = answers[id];
    if (typeof v === "string" && values.includes(v)) n += 1;
  }
  return n;
}

function signSignals(answers: Answers): ExtraSignals {
  return {
    tier1: countTier(answers, TIER1_SIGNS),
    tier2: countTier(answers, TIER2_SIGNS),
    tier3: countTier(answers, TIER3_SIGNS),
  };
}

/**
 * Builds the ignite-namespace answer set the base deriver reads: the 3
 * passthrough ids, the injected energy concern, and the two genuine construct
 * translations (focus-mood -> stress-symptoms, sleep-quality -> sleep-hours)
 * plus the aspiration goal. No fabricated symptom answers — the energy-specific
 * sign severity comes from the `extraSignals` seam.
 */
function syntheticAnswers(answers: Answers): Answers {
  const out: Answers = {};
  for (const id of PASSTHROUGH_IDS) {
    const v = answers[id];
    if (typeof v === "string") out[id] = v;
  }
  // The quiz's entire premise is low energy (see spec §4).
  out["concern"] = "energy";
  const focus = answers["focus-mood"];
  if (typeof focus === "string" && focus in FOCUS_TO_STRESS) {
    out["stress-symptoms"] = FOCUS_TO_STRESS[focus]!;
  }
  const sleep = answers["sleep-quality"];
  if (typeof sleep === "string" && sleep in SLEEP_TO_HOURS) {
    out["sleep-hours"] = SLEEP_TO_HOURS[sleep]!;
  }
  const goal = answers["energy-goal"];
  if (typeof goal === "string" && goal in GOAL_TO_RESULTS) {
    out["results-want"] = GOAL_TO_RESULTS[goal]!;
  }
  return out;
}

export function deriveEnergyProfile(answers: Answers): TSuppressionProfile {
  return deriveIgniteProfile(syntheticAnswers(answers), {
    extraSignals: signSignals(answers),
  });
}
